import {
  type BinaryData,
  useDocumentReferenceForBinaryId,
  useGetBinaryDocument,
} from '@ctw/shared/api/fhir/binaries';
import type { DocumentModel } from '@ctw/shared/api/fhir/models/document';
import { useGetPatientByUpid } from '@ctw/shared/api/fhir/patient';
import { Button } from '@ctw/shared/components/button';
import { ContentError } from '@ctw/shared/components/errors/content-error';
import { ErrorBoundary } from '@ctw/shared/components/errors/error-boundary';
import { Heading } from '@ctw/shared/components/heading';
import { LoadingSpinner } from '@ctw/shared/components/loading-spinner';
import { notify } from '@ctw/shared/components/toast';
import { VerticalDivider } from '@ctw/shared/components/vertical-divider';
import { classifyCdaDocumentError } from '@ctw/shared/content/document-viewer/_internal/renderers/cda-document-error';
import {
  CdaSectionSelectionProvider,
  useCdaSectionSelectionContext,
} from '@ctw/shared/content/document-viewer/_internal/renderers/context/cda-section-selection-provider';
import { DocumentViewerProvider } from '@ctw/shared/content/document-viewer/_internal/renderers/context/document-viewer-provider';
import { CdaSearchInput } from '@ctw/shared/content/document-viewer/_internal/viewer/cda-search-input';
import {
  DocumentViewerControlButton,
  type DocumentViewerControlButtonProps,
} from '@ctw/shared/content/document-viewer/_internal/viewer/document-viewer-control-button';
import { DocumentViewerControlLink } from '@ctw/shared/content/document-viewer/_internal/viewer/document-viewer-control-link';
import { DocumentViewerControlMenu } from '@ctw/shared/content/document-viewer/_internal/viewer/document-viewer-control-menu';
import { DocumentViewerDetailFields } from '@ctw/shared/content/document-viewer/_internal/viewer/document-viewer-detail-fields';
import { DocumentViewerDetailSubsection } from '@ctw/shared/content/document-viewer/_internal/viewer/document-viewer-detail-subsection';
import { DocumentViewerHeaderFields } from '@ctw/shared/content/document-viewer/_internal/viewer/document-viewer-header-fields';
import {
  type DocumentViewerTab,
  DocumentViewerTabMenu,
  DocumentViewerTabs,
} from '@ctw/shared/content/document-viewer/_internal/viewer/document-viewer-tab-menu';
import { DocumentRenderer } from '@ctw/shared/content/document-viewer/document-renderer';
import { SupportFormLink } from '@ctw/shared/content/support-form-link';
import { useCTW } from '@ctw/shared/context/ctw-context';
import { usePatientContextIfAvailable } from '@ctw/shared/context/patient-provider';
import { useTelemetry } from '@ctw/shared/context/telemetry/telemetry-boundary';
import { useZuiContext, type ZusService } from '@ctw/shared/context/zui-provider';
import { useBreakpointQuery, useContainerQuery } from '@ctw/shared/hooks/breakpoints';
import { useInterval } from '@ctw/shared/hooks/use-interval';
import {
  DOCUMENT_VIEWER_CONTROL_BUTTON_BACK_TEST_ID,
  DOCUMENT_VIEWER_CONTROL_BUTTON_CLOSE_TEST_ID,
  DOCUMENT_VIEWER_CONTROL_BUTTON_DOWNLOAD_PDF_TEST_ID,
  DOCUMENT_VIEWER_CONTROL_BUTTON_DOWNLOAD_RAW_TEST_ID,
  DOCUMENT_VIEWER_CONTROL_BUTTON_OPEN_IN_NEW_TAB_TEST_ID,
  DOCUMENT_VIEWER_CONTROL_BUTTON_PRINT_TEST_ID,
  DOCUMENT_VIEWER_CONTROL_MENU_DOWNLOAD_TEST_ID,
  DOCUMENT_VIEWER_SELECTION_CONTEXT_BAR_TEST_ID,
} from '@ctw/shared/test/ids';
import { isCdaContentType } from '@ctw/shared/utils/cda/cda-content-type';
import type { CdaDocument, CdaNonXmlBodyMedia } from '@ctw/shared/utils/cda/types';
import {
  base64ToBlob,
  downloadBlob,
  printBinaryDocument,
  printBlobUrl,
} from '@ctw/shared/utils/documents';
import { getBffApiBaseUrl } from '@ctw/shared/utils/environments';
import { tw, twx } from '@ctw/shared/utils/tailwind';
import { pathWithParameters } from '@ctw/shared/utils/url';
import { prettifyXmlString } from '@ctw/shared/utils/xml';
import {
  faArrowUpRightFromSquare,
  faFileDownload,
  faPrint,
} from '@fortawesome/pro-regular-svg-icons';
import { faArrowLeft, faXmark } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { format } from 'date-fns';
import { compact, orderBy } from 'lodash-es';
import { type MouseEvent, memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';

interface ExportPdfArgs {
  exportAction: 'download' | 'print';
  cdaDocumentSectionIdAllowlist?: Array<string>;
}

// Typed as `ReadonlySet<string>` since `useZuiContext().service` is a plain `string`.
const SERVICES_WITH_FULL_PAGE_DOCUMENT_ROUTE: ReadonlySet<string> = new Set<ZusService>([
  'zap-standalone',
]);

export interface DocumentViewerControls {
  close: boolean;
  openInNewTab: boolean;
  print: boolean;
  download: boolean;
  search: boolean;
  deepLinkSections: boolean;
}

export interface DocumentViewerCdaXmlSource {
  xml: string;
}

// Picks the CDA endpoint pair: `cdaXmlSource` callers (no patient, no ingested
// DocumentReference/Binary) parse and render from raw XML via the `/internal` routes;
// every other caller resolves an already-ingested document via the `/v1` routes.
const cdaEndpointPath = (
  endpoint: 'parsed' | 'pdf',
  cdaXmlSource: DocumentViewerCdaXmlSource | undefined,
) => (cdaXmlSource ? `/internal/cda/${endpoint}` : `/v1/cda/${endpoint}`);

// Identifies an ingested CDA document to the `/v1/cda` routes. A binary with no DocumentReference
// is identified by its own id.
export type CdaDocumentSource = { documentReferenceId: string } | { binaryId: string };

type CdaPdfRequestBody =
  | { xml: string; sectionIds?: Array<string> }
  | (CdaDocumentSource & { patientId: string; sectionIds?: Array<string> });

// Builds the `/cda/pdf` request body to match `cdaEndpointPath`'s endpoint choice: `cdaXmlSource`
// callers send the raw XML, every other caller resolves by patient + document source.
// `null` means neither identifies a document to render, so the caller should skip the fetch.
export const buildCdaPdfRequestBody = (
  cdaXmlSource: DocumentViewerCdaXmlSource | undefined,
  patientId: string | undefined,
  cdaDocumentSource: CdaDocumentSource | undefined,
  sectionIds?: Array<string>,
): CdaPdfRequestBody | null => {
  if (cdaXmlSource) {
    return { xml: cdaXmlSource.xml, sectionIds };
  }
  if (patientId !== undefined && cdaDocumentSource !== undefined) {
    return { ...cdaDocumentSource, patientId, sectionIds };
  }
  return null;
};

// Substitutes a decoded `nonXmlBodyMedia` payload for the Document tab's preview only, so the
// unmodified `DocumentRenderer` dispatches to its native image/PDF/TIFF branch instead of the xml
// branch it would otherwise take for a CDA `nonXMLBody` document. `isXmlDocument`, tab visibility,
// the Download menu, and Print all keep reading the real `binaryData`/`documentReference` —
// nothing else in `DocumentViewer` is aware this substitution happened.
export const buildDocumentRendererBinaryData = (
  binaryData: BinaryData | undefined,
  nonXmlBodyMedia: CdaNonXmlBodyMedia | undefined,
): BinaryData | undefined => {
  if (!(binaryData && nonXmlBodyMedia)) {
    return binaryData;
  }

  return {
    binary: {
      ...binaryData.binary,
      contentType: nonXmlBodyMedia.mediaType,
      blob: base64ToBlob(nonXmlBodyMedia.base64Data, nonXmlBodyMedia.mediaType),
    },
  };
};

// Prefers the DocumentReference's title so the heading matches how the document is named
// everywhere else (e.g. the encounter drawer's document list). A differing CDA <title> stays
// visible in the "Document Title" detail field. A plain-text document without either falls back
// to a generic label rather than "Unknown", since that's the more accurate description of what
// it is.
export const computeDocumentTitle = (
  documentReference: DocumentModel | undefined,
  cdaDocument: CdaDocument | null | undefined,
  isPlainTextDocument: boolean | undefined,
): string =>
  documentReference?.displayTitle ??
  cdaDocument?.metadata?.document?.title ??
  (isPlainTextDocument ? 'Plain Text Document' : 'Unknown');

interface BaseDocumentViewerProps {
  onClose: () => void;
  controls: DocumentViewerControls;
  // Renders and exports the document from raw CDA XML instead of resolving it by patient +
  // DocumentReference id. Only meaningful alongside a synthetic `documentReference`/`binaryData`
  // that has no corresponding ingested FHIR resource to look up.
  cdaXmlSource?: DocumentViewerCdaXmlSource;
}

interface DocumentViewerPropsForDocumentReference extends BaseDocumentViewerProps {
  documentReference: DocumentModel;
  binaryData?: never;
  binaryId?: never;
}

interface DocumentViewerPropsForBinaryId extends BaseDocumentViewerProps {
  documentReference?: never;
  binaryData?: never;
  binaryId: string;
}

interface DocumentViewerPropsForBinaryData extends BaseDocumentViewerProps {
  documentReference?: DocumentModel;
  binaryData: BinaryData;
  binaryId?: never;
}

type DocumentViewerProps =
  | DocumentViewerPropsForDocumentReference
  | DocumentViewerPropsForBinaryId
  | DocumentViewerPropsForBinaryData;

export const DocumentViewer = memo(
  ({
    documentReference: externalDocumentReference,
    binaryId: externalBinaryId,
    binaryData: externalBinaryData,
    onClose,
    controls,
    cdaXmlSource,
  }: DocumentViewerProps) => {
    const { service } = useZuiContext();
    const telemetry = useTelemetry();
    const patientContext = usePatientContextIfAvailable();

    const headerContainerRef = useRef<HTMLDivElement>(null);
    const contentContainerRef = useRef<HTMLDivElement>(null);
    const contentRef = useRef<HTMLDivElement>(null);
    const [cdaDocumentXml, setCdaDocumentXml] = useState<string | null>(null);
    const [prettifiedCdaDocumentXml, setPrettifiedCdaDocumentXml] = useState<string | null>(null);
    const [cdaDocumentPdfPreviewUrl, setCdaDocumentPdfPreviewUrl] = useState<string | null>(null);
    const buildingCdaPdfPreviewRef = useRef(false);
    const [cdaDocument, setCdaDocument] = useState<CdaDocument | null>(null);
    const [cdaParseError, setCdaParseError] = useState<Error | null>(null);
    const [controlMenuIsLoading, setControlMenuIsLoading] = useState(false);
    const buildingCdaDocumentRef = useRef(false);
    const { requestContext } = useCTW();

    const { breakpoints: contentBreakpoints, width: contentContainerWidth } = useContainerQuery({
      containerRef: contentContainerRef,
    });
    const { breakpoints: screenBreakpoints } = useBreakpointQuery();

    /*
     * Note: This is implemented as an interval check instead of using `useScrollSpy` threaded down
     * to the document content scroll container because such threading results in re-renders which
     * cause instability in the document content nodes which affect browser Ctrl+F behavior (APP-4333)
     *
     */
    const [collapsedHeader, setCollapsedHeader] = useState<boolean>(false);
    useInterval({
      intervalInMilliseconds: 20,
      intervalFunction: () => {
        setCollapsedHeader(contentRef.current ? contentRef.current.scrollTop > 35 : false);
      },
    });

    const patientQuery = useGetPatientByUpid({
      upid: externalDocumentReference?.patientUPID ?? '',
      enabled: !patientContext,
    });
    const patient = useMemo(
      () => (patientContext ? patientContext.patient : patientQuery.data),
      [patientContext, patientQuery.data],
    );

    const binaryId = useMemo(
      () => externalBinaryId ?? externalDocumentReference?.binaryId,
      [externalBinaryId, externalDocumentReference?.binaryId],
    );
    const binaryQuery = useGetBinaryDocument(
      binaryId ?? externalDocumentReference?.binaryId ?? '',
      externalBinaryData === undefined && binaryId !== undefined,
    );
    // biome-ignore lint/correctness/useExhaustiveDependencies: Need to include externalDocumentReference
    const binaryData = useMemo(
      () => externalBinaryData ?? binaryQuery.data,
      [externalDocumentReference?.id, externalBinaryData?.binary, binaryQuery.data],
    );

    const documentReferenceQuery = useDocumentReferenceForBinaryId({
      upid: patient?.UPID ?? '',
      binaryId: binaryId ?? '',
      enabled:
        externalDocumentReference === undefined && binaryId !== undefined && Boolean(patient?.UPID),
      includePlainText: true,
    });
    const documentReference = useMemo(
      () => externalDocumentReference ?? documentReferenceQuery.data,
      [externalDocumentReference, documentReferenceQuery.data],
    );
    const isPlainTextDocument = useMemo(
      () => binaryData?.binary?.contentType === 'text/plain',
      [binaryData?.binary?.contentType],
    );
    // `null` means a completed lookup found no DocumentReference; a pending one is `undefined`. The
    // binary query usually settles first, so treating a pending lookup as "no DocumentReference"
    // would parse the document twice.
    const isOrphanedBinary =
      externalDocumentReference === undefined &&
      binaryId !== undefined &&
      documentReferenceQuery.data === null;
    const isXmlDocument = useMemo(
      () =>
        documentReference
          ? documentReference.contentType?.includes('xml')
          : isOrphanedBinary && isCdaContentType(binaryData?.binary.contentType ?? ''),
      [documentReference, isOrphanedBinary, binaryData?.binary.contentType],
    );
    const documentReferenceId = documentReference?.id;
    const cdaDocumentSource = useMemo((): CdaDocumentSource | undefined => {
      if (documentReferenceId !== undefined) {
        return { documentReferenceId };
      }
      return isOrphanedBinary && binaryId !== undefined ? { binaryId } : undefined;
    }, [documentReferenceId, isOrphanedBinary, binaryId]);

    const documentTitle = useMemo(
      () => computeDocumentTitle(documentReference, cdaDocument, isPlainTextDocument),
      [documentReference, cdaDocument, isPlainTextDocument],
    );

    // Set only for a CDA whose entire body is a single previewable `nonXMLBody` entry — see
    // `CdaContent.nonXmlBodyMedia`. Feeds only the Document tab's preview binary below;
    // `isXmlDocument` above (and everything gated on it) still reflects the real document.
    const nonXmlBodyMedia = cdaDocument?.content?.nonXmlBodyMedia;
    const documentRendererBinaryData = useMemo(
      () => buildDocumentRendererBinaryData(binaryData, nonXmlBodyMedia),
      [binaryData, nonXmlBodyMedia],
    );

    // A PDF body already IS a PDF — Print and Download PDF export `documentRendererBinaryData`'s
    // blob directly instead of asking the server to render the section narrative into one, since
    // that narrative is deliberately still the "cannot be displayed" fallback for a PDF body (see
    // `previewableNonXmlBodyMediaTypes` in `server-cda-parser.ts`) and would otherwise disagree
    // with what the Document tab now shows. Image and TIFF bodies keep going through `exportPdf`:
    // an image body's narrative already renders as a real embedded image server-side, and a TIFF
    // body's narrative can never render as one (`@react-pdf/image` supports only JPEG/PNG), so
    // there is no embedded PDF to substitute for either.
    const nonXmlBodyMediaIsPdf = nonXmlBodyMedia?.mediaType === 'application/pdf';

    // A CDA document the BFF refused to parse because it exceeds the hard render size limit.
    // Its controls (XML/PDF tabs, print, Download PDF, search) are gated off since there is no
    // parsed content to act on; only Download XML remains functional (the raw blob is preserved).
    const documentIsTooLargeToRender = useMemo(
      () => cdaDocument !== null && classifyCdaDocumentError(cdaDocument) === 'too-large',
      [cdaDocument],
    );

    // biome-ignore lint/correctness/useExhaustiveDependencies: Intentionally over-specific deps
    const isLoading = useMemo(
      () =>
        binaryQuery.isLoading ||
        patientQuery.isLoading ||
        documentReferenceQuery.isLoading ||
        (isXmlDocument && !cdaDocument && !cdaParseError && buildingCdaDocumentRef.current),
      [
        binaryQuery.isLoading,
        patientQuery.isLoading,
        documentReferenceQuery.isLoading,
        isXmlDocument,
        cdaDocument?.id,
        cdaParseError,
      ],
    );

    // biome-ignore lint/correctness/useExhaustiveDependencies: Intentionally over-specific deps
    const boundaryContext = useMemo(
      () => ({
        documentPatientUpid: patient?.UPID,
        documentReferenceId: documentReference?.id,
        documentBinaryId: binaryData?.binary?.id ?? binaryId,
        documentContentType: binaryData?.binary.contentType,
      }),
      [patient?.UPID, documentReference?.id, binaryData?.binary?.id, binaryId],
    );

    const tabs: Array<DocumentViewerTab> = useMemo(
      (): Array<DocumentViewerTab> =>
        (
          [
            {
              id: 'document',
              label: 'Document',
              onlyVisibleForXmlDocuments: false,
            },
            {
              id: 'details',
              label: 'Details',
              onlyVisibleForXmlDocuments: false,
            },
            {
              id: 'xml',
              label: 'XML',
              onlyVisibleForXmlDocuments: true,
            },
            // A PDF body already IS a PDF, so a tab offering to preview it as one would be
            // redundant and imply the two views might differ — skip it for that case only.
            ...(cdaDocument &&
            requestContext.authTokenState.userType === 'zus' &&
            !nonXmlBodyMediaIsPdf
              ? ([
                  {
                    id: 'pdf_preview',
                    label: 'PDF Preview',
                    onlyVisibleForXmlDocuments: true,
                  },
                ] satisfies Array<DocumentViewerTab>)
              : []),
          ] satisfies Array<DocumentViewerTab>
        ).filter(
          (tab) =>
            (isXmlDocument && !documentIsTooLargeToRender) ||
            tab.onlyVisibleForXmlDocuments === false,
        ),
      [
        requestContext.authTokenState.userType,
        cdaDocument,
        isXmlDocument,
        documentIsTooLargeToRender,
        nonXmlBodyMediaIsPdf,
      ],
    );

    const [selectedTab, internalSetSelectedTab] = useState<DocumentViewerTab>(tabs[0]);

    const setSelectedTab = useCallback(
      (tab: DocumentViewerTab) => {
        internalSetSelectedTab(tab);

        if (tab.id === 'xml' && cdaDocumentXml !== null && prettifiedCdaDocumentXml === null) {
          setPrettifiedCdaDocumentXml(prettifyXmlString(cdaDocumentXml));
        }
      },
      [cdaDocumentXml, prettifiedCdaDocumentXml],
    );

    const downloadRaw = useCallback(
      (event: MouseEvent<HTMLButtonElement>) => {
        event.stopPropagation();

        if (binaryData) {
          downloadBlob(binaryData.binary.blob, `document-${binaryId}`);
        }

        setControlMenuIsLoading(false);
      },
      [binaryData, binaryId],
    );

    const printRaw = useCallback(
      (event: MouseEvent<HTMLButtonElement>) => {
        event.stopPropagation();

        if (binaryData) {
          printBinaryDocument(binaryData.binary.blob, binaryData.binary.contentType);
        }

        setControlMenuIsLoading(false);
      },
      [binaryData],
    );

    // For a CDA whose body already is a PDF (`nonXmlBodyMediaIsPdf`), Print and Download PDF act
    // on the embedded PDF directly (`documentRendererBinaryData`, the same blob the Document tab
    // previews) instead of `exportPdf`'s server round-trip.
    const printEmbeddedPdfBody = useCallback(
      (event: MouseEvent<HTMLButtonElement>) => {
        event.stopPropagation();

        if (documentRendererBinaryData) {
          printBinaryDocument(
            documentRendererBinaryData.binary.blob,
            documentRendererBinaryData.binary.contentType,
          );
        }

        setControlMenuIsLoading(false);
      },
      [documentRendererBinaryData],
    );

    const downloadEmbeddedPdfBody = useCallback(
      (event: MouseEvent<HTMLButtonElement>) => {
        event.stopPropagation();

        const effectiveDocumentReference = externalDocumentReference ?? documentReference;
        if (documentRendererBinaryData) {
          downloadBlob(
            documentRendererBinaryData.binary.blob,
            `${effectiveDocumentReference?.title ?? documentTitle} - Zus ${format(new Date(), 'dd-MM-yyyy')}.pdf`,
          );
        }

        setControlMenuIsLoading(false);
      },
      [documentRendererBinaryData, externalDocumentReference, documentReference, documentTitle],
    );

    const generateServerPdfPreview = useCallback(() => {
      if (buildingCdaPdfPreviewRef.current !== true) {
        buildingCdaPdfPreviewRef.current = true;

        const requestBody = buildCdaPdfRequestBody(cdaXmlSource, patient?.id, cdaDocumentSource);

        if (requestBody !== null) {
          void fetch(
            `${getBffApiBaseUrl(requestContext.env)}${cdaEndpointPath('pdf', cdaXmlSource)}`,
            {
              headers: {
                Authorization: `Bearer ${requestContext.authToken}`,
                'Content-Type': 'application/json',
                Accept: 'application/json',
              },
              method: 'POST',
              body: JSON.stringify(requestBody),
            },
          )
            .then(async (response) => {
              if (!response.ok) {
                notify({
                  type: 'error',
                  title: 'Unable to download PDF',
                  body: 'An error occurred while generating a PDF for download. Please try again.',
                });
              } else {
                setCdaDocumentPdfPreviewUrl(URL.createObjectURL(await response.blob()));
              }
            })
            .finally(() => {
              buildingCdaPdfPreviewRef.current = false;
            });
        }
      }
    }, [
      cdaDocumentSource,
      patient?.id,
      cdaXmlSource,
      requestContext.authToken,
      requestContext.env,
    ]);

    useEffect(() => {
      if (selectedTab.id === 'pdf_preview' && cdaDocumentPdfPreviewUrl === null) {
        generateServerPdfPreview();
      }
    }, [selectedTab, cdaDocumentPdfPreviewUrl, generateServerPdfPreview]);

    // Revokes and clears the generated PDF preview when the document identity changes (the same
    // identity the CDA-parse effect below keys on), and on unmount. Without this,
    // `generateServerPdfPreview` never runs again for a second document mounted into the same
    // `DocumentViewer` instance — it's gated on `cdaDocumentPdfPreviewUrl === null` — so switching
    // documents without unmounting leaves the PDF Preview tab showing whichever document was
    // previewed first, and leaks that document's blob URL for the page's lifetime. Every real
    // product caller (patient document modals, the full-page document route) keys `DocumentViewer`
    // by document id and so remounts instead of hitting this path; the `/internal/document-renderer`
    // sample switcher keeps one instance mounted across every "Render" click, which is why it's
    // the one place this was visible.
    // biome-ignore lint/correctness/useExhaustiveDependencies: externalDocumentReference?.id, documentReference?.id and binaryId are the trigger; the cleanup only reads state via the setCdaDocumentPdfPreviewUrl updater.
    useEffect(() => {
      return () => {
        setCdaDocumentPdfPreviewUrl((previousUrl) => {
          if (previousUrl !== null) {
            URL.revokeObjectURL(previousUrl);
          }
          return null;
        });
      };
    }, [externalDocumentReference?.id, documentReference?.id, binaryId]);

    const exportPdf = useCallback(
      ({ exportAction, cdaDocumentSectionIdAllowlist }: ExportPdfArgs) => {
        const effectiveDocumentReference = externalDocumentReference ?? documentReference;

        const performExportAction = (pdfUrl: string) => {
          if (exportAction === 'print') {
            printBlobUrl(pdfUrl);
          } else {
            const a = document.createElement('a');
            document.body.appendChild(a);
            a.style = 'display: none';
            a.href = pdfUrl;
            a.download = `${cdaDocumentSectionIdAllowlist === undefined ? '' : 'Partial export - '}${effectiveDocumentReference?.title ?? documentTitle} - Zus ${format(new Date(), 'dd-MM-yyyy')}.pdf`;
            a.click();

            setTimeout(() => {
              URL.revokeObjectURL(pdfUrl);
            }, 200);
          }
        };

        const apiBody = buildCdaPdfRequestBody(
          cdaXmlSource,
          patient?.id,
          cdaDocumentSource,
          cdaDocumentSectionIdAllowlist,
        );

        if (apiBody !== null) {
          void fetch(
            `${getBffApiBaseUrl(requestContext.env)}${cdaEndpointPath('pdf', cdaXmlSource)}`,
            {
              headers: {
                Authorization: `Bearer ${requestContext.authToken}`,
                'Content-Type': 'application/json',
                Accept: 'application/json',
              },
              method: 'POST',
              body: JSON.stringify(apiBody),
            },
          )
            .then(async (response) => {
              if (!response.ok) {
                // Without this guard the error body (e.g. a 413 JSON:API payload) would be saved
                // as the "PDF", producing a corrupt file with no indication anything went wrong.
                notify({
                  type: 'error',
                  title: 'Unable to generate PDF',
                  body: 'This document could not be exported to PDF. Try downloading the XML instead.',
                });
                return;
              }

              const blob = await response.blob();
              const url = window.URL.createObjectURL(blob);

              performExportAction(url);
            })
            .finally(() => setControlMenuIsLoading(false));
        }
      },
      [
        externalDocumentReference,
        documentReference,
        documentTitle,
        cdaDocumentSource,
        patient,
        cdaXmlSource,
        requestContext.authToken,
        requestContext.env,
      ],
    );

    // Set document XML when it finished loading (relevant for both client and server-side parsing)
    useEffect(() => {
      if (binaryData !== undefined) {
        setCdaDocumentXml(binaryData.binary.data ?? '');
      }
    }, [binaryData]);

    // biome-ignore lint/correctness/useExhaustiveDependencies: cdaXmlSource is read for its current value here, not a trigger — a fetch starts only when the document source changes.
    useEffect(() => {
      if (
        isXmlDocument &&
        cdaDocumentSource !== undefined &&
        buildingCdaDocumentRef.current === false
      ) {
        const cdaDocumentId =
          'documentReferenceId' in cdaDocumentSource
            ? cdaDocumentSource.documentReferenceId
            : cdaDocumentSource.binaryId;
        buildingCdaDocumentRef.current = true;

        setCdaDocument(null);
        setCdaParseError(null);

        void fetch(
          `${getBffApiBaseUrl(requestContext.env)}${cdaEndpointPath('parsed', cdaXmlSource)}`,
          {
            headers: {
              Authorization: `Bearer ${requestContext.authToken}`,
              'Content-Type': 'application/json',
              Accept: 'application/json',
            },
            method: 'POST',
            body: JSON.stringify(cdaXmlSource ? { xml: cdaXmlSource.xml } : cdaDocumentSource),
          },
        )
          .then(async (response) => {
            const responseJson = await response.json();

            let resolvedCdaDocument: CdaDocument;
            if (!(response.ok && responseJson?.data.attributes)) {
              resolvedCdaDocument = {
                documentIsExcessivelyLarge: false,
                id: cdaDocumentId,
                partial: true,
                onlyNarratives: false,
                parseError:
                  response.status === 413
                    ? {
                        code: 'too-large',
                        message: 'The CDA document exceeds the maximum renderable size',
                      }
                    : {
                        code: 'fetch-failure',
                        message: 'Failed to parse CDA document server-side',
                      },
                detailFields: [],
                detailSubsections: [],
                searchIndexes: [],
                timings: {
                  normalizationDurationMilliseconds: 0,
                  parseToJsonDurationMilliseconds: 0,
                  parseSectionsDurationMilliseconds: 0,
                  mapToDocumentSchemaDurationMilliseconds: 0,
                  assembleDetailFieldsMilliseconds: 0,
                  searchIndexingMilliseconds: 0,
                  totalMilliseconds: 0,
                },
              };
            } else {
              resolvedCdaDocument = responseJson.data.attributes;
            }
            setCdaDocument(resolvedCdaDocument);

            const documentErrorCode = classifyCdaDocumentError(resolvedCdaDocument);
            if (documentErrorCode === 'no-content') {
              telemetry.logger.info('CDA document has no renderable content', {
                documentErrorCode,
                ...cdaDocumentSource,
              });
            } else if (documentErrorCode !== undefined) {
              telemetry.trackError({
                message: 'CDA document could not be displayed',
                error: new Error(`CDA document error: ${documentErrorCode}`),
                context: { documentErrorCode, ...cdaDocumentSource },
              });
            }
          })
          .catch((error) => {
            setCdaParseError(error instanceof Error ? error : new Error(String(error)));
            telemetry.trackError({
              error,
              message: 'Error parsing CDA document',
              // A network/parse throw means the document could not be fetched.
              context: {
                documentErrorCode: 'fetch-failure',
                ...cdaDocumentSource,
              },
            });
          })
          .finally(() => {
            buildingCdaDocumentRef.current = false;
          });
      }
    }, [cdaDocumentSource, isXmlDocument, requestContext.env, requestContext.authToken, telemetry]);

    const documentDetailFields = useMemo(() => {
      if (cdaDocument) {
        return cdaDocument.detailFields;
      }

      // For a CDA whose parse is still in flight, the DocumentReference's
      // promoted metadata (author/date/patient) can disagree with the
      // authoritative parsed-CDA metadata, so don't flash it — wait for
      // cdaDocument. Non-CDA documents never parse a CDA, and a failed parse has
      // no authoritative source coming, so both keep the DocumentReference.
      if (documentReference && (!isXmlDocument || cdaParseError)) {
        return compact([
          documentReference.dateCreated
            ? {
                label: 'Date',
                value: documentReference.dateCreated,
                promoted: true,
                rank: 1,
              }
            : undefined,
          documentReference.subject
            ? { label: 'Patient Name', value: documentReference.subject, promoted: true, rank: 2 }
            : undefined,
          documentReference.authors && documentReference.authors.length > 0
            ? {
                label: 'Author',
                value: documentReference.authors.join(', '),
                promoted: true,
                rank: 3,
              }
            : undefined,
        ]);
      }

      return [];
    }, [cdaDocument, documentReference, isXmlDocument, cdaParseError]);

    if (isLoading) {
      return (
        <article
          className={tw`flex h-full max-h-full min-h-full w-full max-w-full flex-col overflow-hidden`}
        >
          <LoadingSpinner
            message="Loading document..."
            centered={true}
            className={tw`min-h-[20vh]`}
          />
        </article>
      );
    }

    // A document renders without a DocumentReference only once the lookup has confirmed there is
    // none; a non-CDA orphan then has no title or detail fields.
    if (
      !(patient && binaryData?.binary && (documentReference || isOrphanedBinary)) ||
      (isXmlDocument && cdaParseError !== null)
    ) {
      return (
        <article
          className={tw`flex h-full max-h-full min-h-full w-full max-w-full flex-col overflow-hidden p-8`}
        >
          <ContentError title="Unable to load this document">
            <span>If this problem persists </span>
            <SupportFormLink buttonText="contact support" />.
          </ContentError>
        </article>
      );
    }

    return (
      <ErrorBoundary boundaryName="DocumentViewer" initialContext={boundaryContext}>
        {() => (
          <DocumentViewerProvider controls={controls}>
            <CdaSectionSelectionProvider
              sectionIds={cdaDocument?.content?.sections?.map((section) => section.id) ?? []}
            >
              <div
                ref={headerContainerRef}
                className={tw`@container/header flex h-full max-h-full min-h-full w-full max-w-full flex-col overflow-hidden bg-white`}
              >
                <div
                  className={tw`flex w-full min-w-full max-w-full flex-wrap items-center justify-between gap-2 overflow-visible bg-content-main px-5 pt-4`}
                >
                  <div
                    className={tw`flex w-full min-w-full max-w-full @4xl/header:flex-row flex-col gap-2`}
                  >
                    <div className={tw`flex flex-1 items-start gap-2`}>
                      {controls.close && (
                        <DocumentViewerControlButton
                          label={screenBreakpoints.isAtLeast.lg ? 'Close' : 'Back'}
                          testId={
                            screenBreakpoints.isAtLeast.lg
                              ? DOCUMENT_VIEWER_CONTROL_BUTTON_CLOSE_TEST_ID
                              : DOCUMENT_VIEWER_CONTROL_BUTTON_BACK_TEST_ID
                          }
                          icon={screenBreakpoints.isAtLeast.lg ? faXmark : faArrowLeft}
                          telemetryInteractionName="document_viewer_control_click_close"
                          onClick={() => onClose?.()}
                        />
                      )}
                      <Heading
                        level="h1"
                        className={tw`line-clamp-2 font-medium text-white`}
                        // A cdaXmlSource document has no URL that reproduces it, so there is
                        // nothing for a copied link to point back to.
                        deepLink={
                          cdaXmlSource
                            ? undefined
                            : {
                                url: `${window.location.origin}${pathWithParameters(
                                  window.location.href,
                                  {
                                    documentSectionId: null,
                                  },
                                  ['builderId', 'documentId'],
                                )}`,
                                onSuccessToast: {
                                  type: 'success',
                                  title: 'Copied Document Link',
                                  body: 'A shareable link to this document has been copied to your clipboard.',
                                },
                              }
                        }
                      >
                        {documentTitle}
                      </Heading>
                    </div>
                    <div
                      className={tw`flex h-full w-full @4xl/header:max-w-fit flex-1 items-center @4xl/header:justify-end justify-between gap-4`}
                    >
                      <div className={tw`flex flex-0 items-center gap-1`}>
                        {controls.openInNewTab &&
                          documentReference &&
                          SERVICES_WITH_FULL_PAGE_DOCUMENT_ROUTE.has(service) && (
                            <DocumentViewerControlLink
                              label="Open in new Tab"
                              icon={faArrowUpRightFromSquare}
                              testId={DOCUMENT_VIEWER_CONTROL_BUTTON_OPEN_IN_NEW_TAB_TEST_ID}
                              target="_blank"
                              href={`/patient/${patient.UPID}/document/${documentReference.id}`}
                              telemetryInteractionName="document_viewer_control_click_open_in_new_tab"
                            />
                          )}
                        {!isPlainTextDocument && controls.print && !documentIsTooLargeToRender && (
                          <DocumentViewerControlButton
                            label="Print"
                            icon={faPrint}
                            testId={DOCUMENT_VIEWER_CONTROL_BUTTON_PRINT_TEST_ID}
                            onClick={(event) => {
                              event.stopPropagation();
                              setControlMenuIsLoading(true);
                              if (nonXmlBodyMediaIsPdf) {
                                printEmbeddedPdfBody(event);
                              } else if (isXmlDocument) {
                                exportPdf({ exportAction: 'print' });
                              } else {
                                printRaw(event);
                              }
                            }}
                            telemetryInteractionName="document_viewer_control_click_print"
                          />
                        )}
                        {controls.download &&
                          (isXmlDocument ? (
                            <DocumentViewerControlMenu
                              label="Download"
                              icon={faFileDownload}
                              testId={DOCUMENT_VIEWER_CONTROL_MENU_DOWNLOAD_TEST_ID}
                              loading={controlMenuIsLoading}
                              controls={[
                                {
                                  label: 'Download XML',
                                  testId: DOCUMENT_VIEWER_CONTROL_BUTTON_DOWNLOAD_RAW_TEST_ID,
                                  telemetryInteractionName:
                                    'document_viewer_control_click_download_raw',
                                  onClick: (event) => {
                                    setControlMenuIsLoading(true);
                                    downloadRaw(event);
                                  },
                                },
                                // A refused document was never parsed, so there is nothing to
                                // render to PDF — the request would 413 and save the error body.
                                ...(documentIsTooLargeToRender
                                  ? []
                                  : [
                                      {
                                        label: 'Download PDF',
                                        testId: DOCUMENT_VIEWER_CONTROL_BUTTON_DOWNLOAD_PDF_TEST_ID,
                                        telemetryInteractionName:
                                          'document_viewer_control_click_download_pdf',
                                        onClick: (event: MouseEvent<HTMLButtonElement>) => {
                                          event.stopPropagation();
                                          setControlMenuIsLoading(true);
                                          if (nonXmlBodyMediaIsPdf) {
                                            downloadEmbeddedPdfBody(event);
                                          } else {
                                            exportPdf({ exportAction: 'download' });
                                          }
                                        },
                                      } satisfies Omit<DocumentViewerControlButtonProps, 'icon'>,
                                    ]),
                              ]}
                            />
                          ) : (
                            <DocumentViewerControlButton
                              label="Download"
                              testId={DOCUMENT_VIEWER_CONTROL_BUTTON_DOWNLOAD_RAW_TEST_ID}
                              icon={faFileDownload}
                              onClick={downloadRaw}
                              telemetryInteractionName="document_viewer_control_click_download_raw"
                            />
                          ))}
                      </div>
                      {cdaDocument && isXmlDocument && !documentIsTooLargeToRender && (
                        <div
                          className={tw`flex @4xl/header:w-96 w-64 @4xl/header:min-w-96 min-w-[12rem] @4xl/header:max-w-96 max-w-64 items-center justify-end gap-1 @sm:max-xl/header:flex-1`}
                        >
                          <CdaSearchInput cdaDocument={cdaDocument} className={tw`w-full`} />
                        </div>
                      )}
                    </div>
                  </div>
                  <div
                    className={twx(
                      'flex h-[68px] w-full flex-col justify-end pt-2 pb-0 opacity-100 transition-all duration-500',
                      {
                        'pointer-events-none h-0 opacity-0': collapsedHeader,
                        'pb-2': !collapsedHeader,
                      },
                    )}
                  >
                    <DocumentViewerHeaderFields
                      fields={documentDetailFields.filter((field) => field.promoted) ?? []}
                    />
                  </div>

                  {contentContainerWidth > 0 &&
                    (contentBreakpoints.isAtLeast.sm ? (
                      <div className={tw`pt-0`}>
                        <DocumentViewerTabs
                          tabs={tabs}
                          selectedTab={selectedTab}
                          onTabSelected={(id) =>
                            setSelectedTab(tabs.find((tab) => tab.id === id) ?? tabs[0])
                          }
                        />
                      </div>
                    ) : (
                      <div className={tw`flex w-full items-center justify-center px-4 pb-4`}>
                        <DocumentViewerTabMenu
                          tabs={tabs}
                          selectedTab={selectedTab}
                          onTabSelected={(id) =>
                            setSelectedTab(tabs.find((tab) => tab.id === id) ?? tabs[0])
                          }
                        />
                      </div>
                    ))}
                </div>
                <div
                  ref={contentContainerRef}
                  className={tw`flex h-full max-h-full w-full flex-1 overflow-hidden`}
                >
                  <div
                    className={twx('hidden', {
                      contents: selectedTab.id === 'document',
                    })}
                  >
                    <DocumentRenderer
                      key={`${documentReference?.id}-${binaryData?.binary?.id ?? binaryId}-${cdaDocument?.id}`}
                      controls={controls}
                      cdaDocument={cdaDocument}
                      documentId={documentReference?.id ?? binaryId}
                      documentTitle={documentReference?.title ?? documentTitle}
                      binaryData={documentRendererBinaryData ?? binaryData}
                      contentRef={contentRef}
                    />
                    {isXmlDocument && cdaDocument && (
                      <CdaDocumentSelectionContextBar
                        cdaDocument={cdaDocument}
                        downloadPdf={exportPdf}
                      />
                    )}
                  </div>
                  <div
                    className={twx('hidden', {
                      contents: selectedTab.id === 'details',
                    })}
                  >
                    <div className={tw`flex flex-col gap-5 p-5`}>
                      <div>
                        <Heading level="h3">Document Details</Heading>
                        <p className={tw`text-content-subtle text-sm italic`}>
                          This section contains information from when the document was originally
                          created.
                        </p>
                      </div>
                      {documentDetailFields.length > 0 && (
                        <DocumentViewerDetailFields fields={documentDetailFields} />
                      )}
                      {orderBy(cdaDocument?.detailSubsections ?? [], 'rank').map((subsection) => (
                        <DocumentViewerDetailSubsection
                          key={subsection.title}
                          subsection={subsection}
                        />
                      ))}
                    </div>
                  </div>
                  <div
                    className={twx('hidden', {
                      contents: selectedTab.id === 'xml',
                    })}
                  >
                    <pre
                      className={tw`scroll-shadows h-full w-full overflow-auto whitespace-pre-wrap p-5 font-mono`}
                    >
                      {prettifiedCdaDocumentXml}
                    </pre>
                  </div>
                  <div
                    className={twx('hidden', {
                      contents: selectedTab.id === 'pdf_preview',
                    })}
                  >
                    {cdaDocument && selectedTab.id === 'pdf_preview' && (
                      <ErrorBoundary
                        boundaryName="Document Viewer PDF Preview"
                        errorMessage={() => 'Unable to load PDF preview'}
                      >
                        {() =>
                          cdaDocumentPdfPreviewUrl ? (
                            <iframe
                              title="PDF Preview"
                              src={cdaDocumentPdfPreviewUrl}
                              className={tw`pdf-document h-full w-full rounded-md`}
                            />
                          ) : (
                            <LoadingSpinner centered={true} message="Loading PDF preview..." />
                          )
                        }
                      </ErrorBoundary>
                    )}
                  </div>
                </div>
              </div>
            </CdaSectionSelectionProvider>
          </DocumentViewerProvider>
        )}
      </ErrorBoundary>
    );
  },
  (prevProps, nextProps) =>
    prevProps.binaryId === nextProps.binaryId &&
    prevProps.documentReference?.id === nextProps.documentReference?.id &&
    prevProps.binaryData?.binary?.id === nextProps.binaryData?.binary?.id,
);

interface CdaDocumentSelectionContextBarProps {
  cdaDocument: CdaDocument;
  downloadPdf: (args: ExportPdfArgs) => void;
}

const CdaDocumentSelectionContextBar = ({
  cdaDocument,
  downloadPdf,
}: CdaDocumentSelectionContextBarProps) => {
  const { selectedSectionIds, clearSelectedSectionIds, selectAllSectionIds } =
    useCdaSectionSelectionContext();

  const [downloadButtonSectionCount, setDownloadButtonSectionCount] = useState(
    selectedSectionIds.length,
  );

  useEffect(() => {
    // When going from N->0 selected sections (which implicitly hides the selection context bar),
    // defer the update to the selection count so that as the context bar fades out it still shows
    // the previously selected count instead of flashing to `0` while it animates out
    if (selectedSectionIds.length === 0) {
      setTimeout(() => setDownloadButtonSectionCount(selectedSectionIds.length), 300);
    } else {
      setDownloadButtonSectionCount(selectedSectionIds.length);
    }
  }, [selectedSectionIds]);

  return (
    <aside
      data-testid={DOCUMENT_VIEWER_SELECTION_CONTEXT_BAR_TEST_ID}
      className={twx(
        'fixed right-0 left-0 mx-auto h-[52px] max-h-[52px] min-h-[52px] min-w-[240px] max-w-[680px] transition-all duration-300',
        {
          'bottom-[32px] opacity-100': selectedSectionIds.length > 0,
          'pointer-events-none -bottom-[0px] opacity-0': selectedSectionIds.length === 0,
        },
      )}
    >
      <div
        className={tw`mx-[32px] flex h-full w-full items-center rounded-md border border-border-main bg-white/90 px-2 py-3 shadow-2xl backdrop-blur-[2px]`}
      >
        <div className={tw`flex h-full min-h-full items-center`}>
          <Button
            type="button"
            ariaLabel="Clear Selected Sections"
            variant="unstyled"
            size="sm"
            className={tw`flex h-[30px] max-h-[30px] min-h-[30px] w-[30px] min-w-[30px] max-w-[30px] items-center justify-center rounded-full bg-white p-2 text-content-main shadow-none transition-all duration-300 hover:bg-content-subtle/10 active:scale-[.98] active:bg-content-subtle/15`}
            telemetryInteractionName="document_viewer_deselect_all_sections"
            telemetryContext={{
              documentReferenceId: cdaDocument.id,
              sectionCount: selectedSectionIds.length,
            }}
            onClick={clearSelectedSectionIds}
          >
            <FontAwesomeIcon icon={faXmark} className={tw`h-4 w-4`} />
          </Button>
          <VerticalDivider className={tw`mx-1 border-border-main`} />
          <Button
            type="button"
            ariaLabel="Select All"
            variant="link"
            size="sm"
            className={tw`flex items-center justify-center bg-white p-2 text-content-main text-sm leading-tight shadow-none transition-all duration-300 hover:bg-content-subtle/10 hover:no-underline active:scale-[.98] active:bg-content-subtle/15`}
            telemetryInteractionName="document_viewer_select_all_sections"
            telemetryContext={{
              documentReferenceId: cdaDocument.id,
              sectionCount: cdaDocument.content?.sections.length ?? 0,
            }}
            onClick={selectAllSectionIds}
          >
            <span>Select All</span>
          </Button>
        </div>
        <div className={tw`flex flex-1 items-center justify-end`}>
          <Button
            type="button"
            ariaLabel="Download Selected sections as PDF"
            variant="primary"
            size="sm"
            className={tw`flex items-center gap-1`}
            telemetryInteractionName="document_viewer_download_selected_sections"
            telemetryContext={{
              documentReferenceId: cdaDocument.id,
              sectionCount: selectedSectionIds.length,
            }}
            onClick={() =>
              downloadPdf({
                exportAction: 'download',
                cdaDocumentSectionIdAllowlist: selectedSectionIds,
              })
            }
          >
            <FontAwesomeIcon icon={faFileDownload} className={tw`h-4 w-4`} />
            <span className={tw`font-medium text-sm`}>
              Download {downloadButtonSectionCount}&nbsp;
              {downloadButtonSectionCount <= 1 ? 'Section' : 'Sections'}
            </span>
          </Button>
        </div>
      </div>
    </aside>
  );
};
